import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import api from '../utils/api';
import AppLoadingState, { LoadingSpinnerRing } from './common/AppLoadingState';

const BlogCard = ({ slug, title, excerpt, date, read_time, category, image_url, gradient }) => {
  const navigate = useNavigate();
  const categories = category.split(',').map(cat => cat.trim()).filter(Boolean);
  
  // Format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  return (
    <div 
      className="bg-white/5 backdrop-blur-xl rounded-2xl p-1 cursor-pointer transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      onClick={() => navigate(`/blog/${slug}`)}
    >
      <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-8 h-full`}>
        <div className="flex flex-col h-full">
          <div className="mb-6">
            <img src={image_url} alt={title} className="w-full h-48 object-cover rounded-xl mb-6" />
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-gray-400">{formatDate(date)}</span>
              <span className="text-sm text-gray-400">•</span>
              <span className="text-sm text-gray-400">{read_time} min read</span>
            </div>
            <h3 className="text-2xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-gray-300 mb-4">{excerpt}</p>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-2">
                {categories.map((cat, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-300"
                  >
                    {cat}
                </span>
                ))}
              </div>
              <span className="text-[#FF56A5] hover:text-[#E852FF] transition-colors">
                Read more →
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CategoryFilter = ({ categories, selectedCategory, onSelect }) => (
  <div className="flex flex-wrap gap-3 justify-center mb-12">
    <button
      onClick={() => onSelect(null)}
      className={`px-4 py-2 rounded-full transition-all duration-200 ${
        selectedCategory === null
          ? 'bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] text-white'
          : 'bg-white/5 text-gray-400 hover:bg-white/10'
      }`}
    >
      All
    </button>
    {categories.map((category) => (
      <button
        key={category}
        onClick={() => onSelect(category)}
        className={`px-4 py-2 rounded-full transition-all duration-200 ${
          selectedCategory === category
            ? 'bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] text-white'
            : 'bg-white/5 text-gray-400 hover:bg-white/10'
        }`}
      >
        {category}
      </button>
    ))}
  </div>
);

const NoPostsMessage = ({ isFiltered }) => (
  <div className="text-center py-16">
    <svg 
      className="w-16 h-16 mx-auto mb-4 text-gray-400"
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={1.5}
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" 
      />
    </svg>
    <h3 className="text-2xl font-semibold text-white mb-2">
      {isFiltered ? 'No matching posts found' : 'No blog posts yet'}
    </h3>
    <p className="text-gray-400 max-w-md mx-auto">
      {isFiltered 
        ? "Try adjusting your search or filter criteria to find what you're looking for."
        : "Check back later for insightful articles about AI communication and technology."}
    </p>
  </div>
);

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Helper function to validate business email
  const isBusinessEmail = (email) => {
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
      'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
      'gmx.com', 'live.com', 'msn.com'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !freeEmailProviders.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!isBusinessEmail(email)) {
      setError('Please use your business email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.subscribeToNewsletter(email);
      setSuccess(true);
      setEmail('');
    } catch (error) {
      setError(error.message || 'Failed to subscribe');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
      <div className="flex-1 relative">
        <input
          type="email"
          placeholder="Enter your business email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-6 py-3 bg-white/10 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF56A5]"
          required
          disabled={isSubmitting}
        />
      </div>
      <button
        type="submit"
        className="px-8 py-3 bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] rounded-full text-white font-medium hover:from-[#FF56A5]/90 hover:via-[#E852FF]/90 hover:to-[#9F44F2]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <LoadingSpinnerRing tone="onDark" className="!h-5 !w-5" />
        ) : (
          'Subscribe'
        )}
      </button>
      {error && (
        <div className="absolute -bottom-8 left-0 right-0 text-red-500 text-sm text-center">
          {error}
        </div>
      )}
      {success && (
        <div className="absolute -bottom-8 left-0 right-0 text-green-500 text-sm text-center">
          Thanks for subscribing! Stay tuned—we'll be in touch soon!
        </div>
      )}
    </form>
  );
};

const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const posts = await api.getBlogPosts(true); // Only get published posts
        setBlogPosts(posts);
        
        // Extract unique categories from all posts
        const allCategories = posts.reduce((acc, post) => {
          const postCategories = post.category.split(',').map(cat => cat.trim()).filter(Boolean);
          return [...acc, ...postCategories];
        }, []);
        const uniqueCategories = [...new Set(allCategories)];
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const filteredPosts = blogPosts
    .filter(post => !selectedCategory || post.category.split(',').map(cat => cat.trim()).includes(selectedCategory))
    .filter(post => 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    );

  if (loading) {
    return (
      <div className="landing-page bg-background min-h-screen">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <AppLoadingState
            layout="compact"
            tone="marketing"
            label="Loading articles…"
            className="min-h-[40vh]"
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page bg-background min-h-screen">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
            <p className="text-gray-400">{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="landing-page bg-background min-h-screen">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Latest{' '}
            <span className="bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] bg-clip-text text-transparent">
              Insights
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Stay updated with the latest trends and insights in AI-powered communication
          </p>
        </div>

        {/* Search Bar */}
        {blogPosts.length > 0 && (
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-3 bg-white/5 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF56A5]"
            />
            <svg
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        )}

        {/* Category Filter */}
        {blogPosts.length > 0 && (
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
        />
        )}

        {/* Blog Posts Grid or No Posts Message */}
        {blogPosts.length === 0 ? (
          <NoPostsMessage isFiltered={false} />
        ) : filteredPosts.length === 0 ? (
          <NoPostsMessage isFiltered={true} />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredPosts.map((post, index) => (
            <BlogCard key={index} {...post} />
          ))}
        </div>
        )}

        {/* Newsletter Section */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
            <div className="bg-gradient-to-br from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-semibold text-white mb-4">
                Subscribe to Our Newsletter
              </h2>
              <p className="text-gray-300 mb-6">
                Get the latest insights and updates delivered directly to your inbox.
              </p>
              <NewsletterForm />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Blog; 